import type { Building } from "building-types/building/buildingTypes";
import { exteriorMaterialChoices } from "building-types/materials/exteriorMaterialSlotNames";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { InteriorMaterial } from "building-types/materials/interiorPaletteTypes";
import type { Palette } from "building-types/materials/palette";
import { PaletteType } from "building-types/materials/paletteType";
import { interiorMaterialChoices } from "building-types/settings/interiorMaterialSettings";
import { ColorIdToColor } from "common/materials/library/colorIdToColor";
import { ColorIdToName } from "common/materials/library/colorIdToName";
import { DesignMaterialPath } from "common/materials/library/designMaterialPath";
import { DesignMaterialTemplateColorLibs } from "common/materials/library/designMaterialTemplateColorLibs";
import { MasonryNameById } from "common/materials/library/masonryNameById";
import { TemplateIdToName } from "common/materials/library/templateIdToName";
import type { DesignMaterialPathNode } from "common/materials/materialLibraryUtil";
import { addColorPathStringsToList, colorIdFromColorPath } from "common/materials/materialLibraryUtil";
import type { MaterialSrc } from "common/materials/materialSrc";
import {
  getInteriorMaterialSrcByKindNoFallback,
  getMaterialSrcByKindNoFallback,
  nonEmptyPaletteMaterialChoices,
} from "doc/materialLookup";
import type { OptionProps } from "ui/forms/Select";

function isAdminOnlyTemplate(templateName: string): boolean {
  return templateName.startsWith("X_");
}

export function getMaterialColorOptions(
  templateId: number,
  layerConfigChosen: boolean,
  userInput: string,
): OptionProps[] {
  const undefinedColor = { value: "", label: "None" };
  if (templateId === undefined || !TemplateIdToName.hasOwnProperty(templateId)) {
    return [undefinedColor];
  }
  const templateName = TemplateIdToName[templateId.toString()];
  if (!DesignMaterialTemplateColorLibs.hasOwnProperty(templateName)) {
    return [undefinedColor];
  }
  const colorLibIds: string[] = DesignMaterialTemplateColorLibs[templateName];
  const normalizedUserInput = userInput.toLowerCase().replace(/ /g, "_");
  const pathStrings: string[] = [];
  addColorPathStringsToList(pathStrings, colorLibIds, "");
  const matchingColorOptions: OptionProps[] = pathStrings
    .filter((path) => normalizedUserInput === "" || path.toLowerCase().indexOf(normalizedUserInput) >= 0)
    .map((path) => {
      const value: number = colorIdFromColorPath(path) as number;
      return { value, label: materialColorLabelPrettyName(path), color: ColorIdToColor[value] };
    });

  if (
    DesignMaterialPath[templateName].hasOwnProperty("Default") ||
    (layerConfigChosen &&
      (normalizedUserInput === "" || undefinedColor.label.toLowerCase().indexOf(normalizedUserInput) >= 0))
  ) {
    matchingColorOptions.splice(0, 0, undefinedColor);
  }
  return matchingColorOptions;
}

function addMaterialPathStringsToList(list: string[], libNode: DesignMaterialPathNode, pathPrefix: string) {
  if ((libNode as MaterialSrc).templateId !== undefined) {
    list.push(pathPrefix);
    return;
  }
  for (const childNodeName of Object.keys(libNode)) {
    addMaterialPathStringsToList(list, libNode[childNodeName], `${pathPrefix}.${childNodeName}`);
  }
}

const materialTemplateAutofillOptions = Object.keys(DesignMaterialPath).map((templateName) => {
  return { value: templateName, label: templateName.replace(/_/g, " "), intermediate: true };
});

const materialTemplateAutofillOptionsNoColor = Object.keys(DesignMaterialPath).map((templateName) => {
  const hasDefault = DesignMaterialPath[templateName].hasOwnProperty("Default");
  const hasLayers = DesignMaterialPath[templateName].hasOwnProperty("L");
  const onlyHasColorVariations = !hasLayers && !hasDefault; // We can pick the template directly.
  const suffix = onlyHasColorVariations ? ".C" : "";
  return {
    value: templateName + suffix,
    label: templateName.replace(/_/g, " ") + suffix,
    intermediate: !onlyHasColorVariations,
  };
});

function getMaterialPathAutofillOptionsInternal(
  userInput: string,
  includeColors: boolean,
  allowAdminOnlyTemplates: boolean,
): OptionProps[] {
  const normalizedUserInput = userInput.toLowerCase().replace(/ /g, "_");
  for (const templateName of Object.keys(DesignMaterialPath)) {
    if (!allowAdminOnlyTemplates && isAdminOnlyTemplate(templateName)) {
      continue;
    }
    const templateNameLower = templateName.toLowerCase();
    if (normalizedUserInput === templateNameLower || normalizedUserInput.startsWith(templateNameLower + ".")) {
      const pathStrings: string[] = [];
      if (DesignMaterialPath[templateName].hasOwnProperty("Default")) {
        addMaterialPathStringsToList(
          pathStrings,
          DesignMaterialPath[templateName]["Default"],
          templateName + ".Default",
        );
      }
      if (DesignMaterialPath[templateName].hasOwnProperty("L")) {
        addMaterialPathStringsToList(pathStrings, DesignMaterialPath[templateName]["L"], templateName + ".L");
      }
      if (DesignMaterialTemplateColorLibs.hasOwnProperty(templateName)) {
        if (includeColors) {
          addColorPathStringsToList(pathStrings, DesignMaterialTemplateColorLibs[templateName], templateName + ".C.");
        } else {
          pathStrings.push(templateName + ".C");
        }
      }
      return pathStrings
        .filter((path) => normalizedUserInput === "" || path.toLowerCase().indexOf(normalizedUserInput) >= 0)
        .map((path) => {
          return { value: path, label: path.replace(/_/g, " ") };
        });
    }
  }
  const templateOptions = includeColors ? materialTemplateAutofillOptions : materialTemplateAutofillOptionsNoColor;
  const userAccessibleTemplateOptions = allowAdminOnlyTemplates
    ? templateOptions
    : templateOptions.filter((option) => !isAdminOnlyTemplate(option.value));
  if (normalizedUserInput === "") {
    return userAccessibleTemplateOptions;
  }
  return userAccessibleTemplateOptions.filter((option) => option.value.toLowerCase().indexOf(normalizedUserInput) >= 0);
}

export function getMaterialPathAutofillOptions(
  userInput: string,
  allowAdminOnlyTemplates: boolean = false,
): OptionProps[] {
  return getMaterialPathAutofillOptionsInternal(userInput, true, allowAdminOnlyTemplates);
}

export function getMaterialPathAutofillOptionsNoColor(userInput: string, allowAdminOnlyTemplates: boolean = false) {
  return getMaterialPathAutofillOptionsInternal(userInput, false, allowAdminOnlyTemplates);
}

export function getMasonryAutofillOptions(userInput: string): OptionProps[] {
  const normalizedUserInput = userInput.toLowerCase().replace(/ /g, "_");
  const masonryOptions = MasonryNameById.filter(
    (key) => normalizedUserInput === "" || key.toLowerCase().indexOf(normalizedUserInput) >= 0,
  ).map((path) => {
    return { value: path, label: path.replace(/_/g, " ") };
  });
  return [...getMaterialPathAutofillOptions(userInput), ...masonryOptions];
}

const systemColorsPrefix = "SystemColors.";

export function materialColorLabelPrettyName(colorName: string): string {
  if (!colorName || typeof colorName !== "string") {
    return "None";
  }

  if (colorName.startsWith(systemColorsPrefix)) {
    return colorName.substring(systemColorsPrefix.length).replace(/_/g, " ");
  }

  return colorName.replace(/_/g, " ");
}

export function materialColorIdPrettyName(
  colorId:
    | string
    | undefined /* Takes in string just to be compatible with Select element renamer function signature */,
) {
  if (colorId === undefined || colorId === null) {
    return "None";
  }
  return materialColorLabelPrettyName(ColorIdToName[Number(colorId)]);
}

export const SELECT_MATERIAL_PLACEHOLDER = "Select a material...";

export interface FilterEmptyPaletteMaterialChoicesOptions {
  readonly filterEmpty?: boolean;
  readonly paletteType?: PaletteType;
}

function paletteSlotHasAssignedMaterial(palette: Palette, slot: string): boolean {
  if (palette.type === PaletteType.EXTERIOR_PALETTE) {
    if (!exteriorMaterialChoices.includes(slot as ExteriorMaterial)) {
      return false;
    }
    return getMaterialSrcByKindNoFallback(palette, slot as ExteriorMaterial) !== undefined;
  }
  if (!interiorMaterialChoices.includes(slot as InteriorMaterial)) {
    return false;
  }
  return getInteriorMaterialSrcByKindNoFallback(palette, slot as InteriorMaterial) !== undefined;
}

function paletteMaterialSlotHasAssignment(
  building: Pick<Building, "exteriorPalette" | "interiorPalette">,
  slot: string,
): boolean {
  return [building.exteriorPalette, building.interiorPalette].some(
    (palette) => palette !== undefined && paletteSlotHasAssignedMaterial(palette, slot),
  );
}

/** Drops palette slots with no assigned material when `filterEmpty: true`. */
export function filterEmptyPaletteMaterialChoices(
  building: Pick<Building, "exteriorPalette" | "interiorPalette"> | undefined,
  materialChoices: readonly (ExteriorMaterial | InteriorMaterial)[],
  currentMaterial?: string,
  options: FilterEmptyPaletteMaterialChoicesOptions = {},
): (ExteriorMaterial | InteriorMaterial)[] {
  if (options.filterEmpty !== true || !building) {
    return [...materialChoices];
  }

  if (options.paletteType === PaletteType.EXTERIOR_PALETTE && building.exteriorPalette) {
    return nonEmptyPaletteMaterialChoices(
      building.exteriorPalette,
      materialChoices as readonly ExteriorMaterial[],
      currentMaterial as ExteriorMaterial | undefined,
    );
  }

  if (options.paletteType === PaletteType.INTERIOR_PALETTE && building.interiorPalette) {
    return nonEmptyPaletteMaterialChoices(
      building.interiorPalette,
      materialChoices as readonly InteriorMaterial[],
      currentMaterial as InteriorMaterial | undefined,
    );
  }

  return materialChoices.filter((slot) => {
    if (currentMaterial !== undefined && slot === currentMaterial) {
      return true;
    }
    return paletteMaterialSlotHasAssignment(building, slot);
  });
}
